import { useEffect, useState, useMemo } from "react";
import { usePageTitle } from "../layouts/PageTitleContext";
import useBreadcrumbSetup from "../components/useBreadcrumbSetup";
import EntityTable from "../components/Listing/EntityTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHourglassStart,
  faLock,
  faCheckCircle,
  faUserSlash,
  faEdit,
  faMoneyBill,
  faUser,
  faGear,
  faCoins,
  faTrash,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { formatRelativeDate, isToday } from "../utils/FormatDate";
import { NavLink } from "react-router-dom";
import PatientImage from "../components/Patient/PatientImage";
import { eventBus } from "../bus/eventBus";
import { useTranslation } from "react-i18next";
import InputText from "../components/Form/Fields/InputText";
import Select from "../components/Form/Fields/Select";
import InputPatient from "../components/Form/Fields/InputPatient";
import { getOrderStatusOptions } from "../config/OrderStatus";
import { formatPrice } from "../utils/FormatPrice";
import NewInvoiceModal from "../components/Modal/NewInvoiceModal";
import CreateReturnModal from "../components/Modal/CreateReturnModal";
import ConfirmModal from "../components/Modal/ConfirmModal";
import { getOrderColumns, StatusBadge, formatAmount } from "../components/Order/orderColumns";
import useOrderColumns from "../hooks/orderColumns";
import { useAuth } from "../context/AuthProvider";
import { API_BASE_URL } from "../api";

// products listed at the bottom of a sale's card (the rest summed up as "+ N autres produits")
const CARD_ITEMS_SHOWN = 3;

export default function Orders() {
  const { t } = useTranslation();
  const { setTitle, setTitleIcon } = usePageTitle();
  const { hasRight } = useAuth();
  const canCancel = hasRight('ORDERS_CANCEL');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [showCreateReturnModal, setShowCreateReturnModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const handleConfirmCancel = () => {
    axios.patch(
      `${API_BASE_URL}/api/v1/orders/${orderToCancel}/status`,
      { status: "CANCELED" },
      { withCredentials: true }
    ).then(() => {
      setShowCancelModal(false);
      setOrderToCancel(null);
      setReloadKey((prev) => prev + 1);
    }).catch((error) => console.error("Failed to cancel order:", error));
  };

  const columns = useOrderColumns({
      onNewInvoice: (row) => {
        setSelectedOrder(row);
        setShowNewInvoiceModal(true);
      },
      onCreateReturn: (row) => {
        setSelectedOrder(row);
        setShowCreateReturnModal(true);
      },
      onCancelOrder: (orderId) => {
        setOrderToCancel(orderId);
        setShowCancelModal(true);
      },
      onReload: () => {
        setReloadKey((prev) => prev + 1);
      }
  });

  // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
  // — reuses the same StatusBadge/formatAmount as the table columns, and the actions
  // column's own dropdown (found in `columns`) so both views offer identical actions.
  const actionsColumn = columns.find((c) => c.accessor === "actions");
  const renderOrderCard = (row) => (
    <div className="card h-100 shadow-sm">
      <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
        {/* header: sale number + date on the left, status on the right, same line */}
        <div className="d-flex align-items-start justify-content-between" style={{ gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <NavLink
              to={`/order/${row.id}`}
              style={{ fontSize: 13.5, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {row.reference}
            </NavLink>
            <span style={{ fontSize: 11.5, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatRelativeDate(row.createdAt)}</span>
          </div>
          <div style={{ flexShrink: 0 }}><StatusBadge status={row.status} /></div>
        </div>

        {/* the sale's products (first ones, then "+ N autres"), then the patient, the price and actions */}
        {row.items?.length > 0 && (
          <div style={{ borderTop: "1px solid #f1f3f5", borderBottom: "1px solid #f1f3f5", marginTop: 6, paddingBlock: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {row.items.slice(0, CARD_ITEMS_SHOWN).map((item) => (
              <div key={item.id} className="d-flex align-items-baseline" style={{ gap: 6, fontSize: 12 }}>
                <span style={{ color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>{item.quantity}×</span>
                <span
                  style={{ color: "#111827", fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={item.orderable?.name}
                >
                  {item.orderable?.name || "—"}
                </span>
                <span style={{ color: "#6b7280", flexShrink: 0 }}>
                  {formatAmount(Number(item.unitPrice || 0) * item.quantity)}
                </span>
              </div>
            ))}
            {row.items.length > CARD_ITEMS_SHOWN && (
              <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
                + {row.items.length - CARD_ITEMS_SHOWN} autre{row.items.length - CARD_ITEMS_SHOWN > 1 ? "s" : ""} produit{row.items.length - CARD_ITEMS_SHOWN > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}

        {row.patient ? (
          <div className="d-flex align-items-center gap-2" style={{ marginTop: 4 }}>
            <PatientImage patient={row.patient} size={28} />
            <span style={{ fontSize: 12.5, color: "#374151" }}>
              {row.patient.firstName}{row.patient.lastName ? ` ${row.patient.lastName[0]}.` : ''}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 4 }}>{t("Visitor")}</span>
        )}

        <div className="d-flex align-items-center justify-content-between" style={{ marginTop: "auto", paddingTop: 6 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              {formatAmount(row.total)}
            </span>
            <span style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 500 }}>dhs</span>
          </div>

          {actionsColumn?.Cell(row)}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    setTitle(`${t("Orders")}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">Historique des ventes</span>`);
    document.title = t("Orders");
    setTitleIcon('ti-cash-register');
    return () => setTitleIcon(null);
  }, [setTitle, setTitleIcon, t]);

  useBreadcrumbSetup([{ label: t("Orders") }]);

  useEffect(() => {
    const handler = () => setReloadKey(prev => prev + 1);
    eventBus.on("orderCreated", handler);
    return () => eventBus.off("orderCreated", handler);
  }, []);

  

  return (
    <>
      <EntityTable
        key={reloadKey}
        endpoint={`${API_BASE_URL}/api/v1/getOrders`}
        columns={columns}
        // today's sales get a light tint, to spot them at a glance
        rowClassName={(row) => (isToday(row.createdAt) ? 'row-today' : '')}
        defaultSort="id"
        defaultDirection="desc"
        pageSize={20}
        emptyMessage="Aucune vente"
        searchable={true}
        searchPlaceholder="Rechercher une vente..."
        multiSelect={true}
        enableCardView={true}
        renderCard={renderOrderCard}
        selectionActions={[
          ...(canCancel ? [{
            label: "Annuler",
            icon: faLock,
            variant: "danger",
            onClick: (ids) => {
              if (window.confirm(`Annuler ${ids.length} vente(s) ?`)) {
                setReloadKey(prev => prev + 1);
              }
            },
          }] : []),
          {
            label: "Exporter",
            icon: faFileExport,
            onClick: (ids) => {
              console.log("Export", ids);
            },
          },
        ]}
      />

      <NewInvoiceModal
        order={selectedOrder}
        show={showNewInvoiceModal}
        onClose={() => setShowNewInvoiceModal(false)}
      />

      <CreateReturnModal
        order={selectedOrder}
        show={showCreateReturnModal}
        onClose={() => { setShowCreateReturnModal(false); setSelectedOrder(null); }}
        onSave={() => setReloadKey(prev => prev + 1)}
      />

      <ConfirmModal
        id="cancelOrderModal"
        title="Annuler cette vente ?"
        description="Cette action est irréversible. La vente sera marquée comme annulée."
        confirmLabel="Oui, annuler"
        cancelLabel="Non, garder"
        show={showCancelModal}
        onClose={() => { setShowCancelModal(false); setOrderToCancel(null); }}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
}